import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Popover from '@mui/material/Popover';
import type { Chat } from '@trinity/chat';
import { ChatCitationType } from '@trinity/chat';
import { toTitleCase } from '@trinity/chat-components/utils';
import { CitationsTable } from './Citation.Table';

type Props = {
  item: Chat.Citation<ChatCitationType.DataAgentQueryResults>;
  snippet: Chat.CitationSnippet<ChatCitationType.DataAgentQueryResults>;
};

function formatValue(value: Chat.DataAgentQueryResultsValue): string {
  if (value === null) {
    return 'null';
  }

  return String(value);
}

type DescriptorLabelProps = {
  label: string;
  dataSourceName: string;
  valueType: 'field' | 'metric';
  dataSourceDescription: string | null;
};

function DescriptorLabel(props: DescriptorLabelProps): React.JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <DescriptorButton type="button" onClick={(event) => setAnchorEl(event.currentTarget)}>
        {props.label}
      </DescriptorButton>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <PopoverContent>
          <PopoverRow>
            <PopoverLabel>Value Type</PopoverLabel>
            <PopoverValue>{toTitleCase(props.valueType)}</PopoverValue>
          </PopoverRow>
          <PopoverRow>
            <PopoverLabel>Data Source Name</PopoverLabel>
            <PopoverValue>{props.dataSourceName}</PopoverValue>
          </PopoverRow>
          <PopoverRow>
            <PopoverLabel>Data Source Description</PopoverLabel>
            <PopoverValue>{props.dataSourceDescription ?? 'Unknown'}</PopoverValue>
          </PopoverRow>
        </PopoverContent>
      </Popover>
    </>
  );
}

function buildTableSnippetRows(data: Chat.DataAgentQueryResultsTableSnippetData) {
  return [
    { name: 'Record Name', value: data.name },
    { name: 'Underlying Tables', value: data.sourceTables ?? 'Unknown' },
    { name: 'Last Refresh Date', value: data.lastUpdatedOn ?? 'Unknown' },
    { name: 'Max Maturity Date', value: data.maxMaturityDate ?? 'Unknown' },
  ];
}

function buildRecordSnippetRows(
  item: Chat.Citation<ChatCitationType.DataAgentQueryResults>,
  data: Chat.DataAgentQueryResultsRecordSnippetData
) {
  return data.values.map((recordValue) => {
    const descriptor =
      recordValue.descriptorType === 'metric'
        ? item.metadata.metricDescriptorByKey[recordValue.descriptorKey]
        : item.metadata.fieldDescriptorByKey[recordValue.descriptorKey];

    return {
      name: (
        <DescriptorLabel
          label={descriptor?.name ?? recordValue.descriptorKey}
          dataSourceName={descriptor?.sourceTable ?? 'Unknown'}
          valueType={recordValue.descriptorType}
          dataSourceDescription={descriptor?.dataSourceDescription ?? null}
        />
      ),
      value: formatValue(recordValue.value),
    };
  });
}

export function DataAgentQueryResultsCitation(props: Props): React.JSX.Element | null {
  const rows = useMemo(() => {
    if (props.snippet.metadata.snippetKind === 'table') {
      const tableData = props.item.metadata.tableSnippetDataByKey[props.snippet.metadata.tableKey];
      if (!tableData) {
        return [];
      }

      return buildTableSnippetRows(tableData);
    }

    const recordData = props.item.metadata.recordSnippetDataByKey[props.snippet.metadata.recordKey];
    if (!recordData) {
      return [];
    }

    return buildRecordSnippetRows(props.item, recordData);
  }, [props.item, props.snippet]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <Container>
      <CitationsTable items={rows} />
    </Container>
  );
}

const Container = styled('div')({
  marginTop: 10,
});

const DescriptorButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.palette.hyperlink,
  cursor: 'pointer',
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  padding: 0,
  textAlign: 'left',
}));

const PopoverContent = styled('div')({
  display: 'grid',
  gap: 8,
  maxWidth: 380,
  padding: 12,
});

const PopoverRow = styled('div')({
  display: 'grid',
  gap: 2,
});

const PopoverLabel = styled('div')(({ theme }) => ({
  color: theme.palette.gray.dark1,
  fontFamily: theme.fonts.semiBold,
  fontSize: 12,
}));

const PopoverValue = styled('div')(({ theme }) => ({
  color: theme.palette.black.main,
  fontFamily: theme.fonts.regular,
  fontSize: 13,
  lineHeight: '18px',
}));
